import { useAppSelector } from "@/stores/hook";
import { useCurrentTenant } from "@/features/settings/api/useCurrentTenant";
import { selectRbacContext } from "@/rbac/rbacSlice";
import type { RbacUserContext } from "@/rbac/utils";

/**
 * Super Admins only (owner directive 2026-09-18): the tenant admin flag, a
 * platform role, or the "Super Admin" role name — the same test the server
 * applies in `requireAISuperAdmin` and the live voice socket. This hook only
 * hides the UI; the server is the boundary.
 */
export function isSuperAdminContext(ctx: RbacUserContext | null | undefined): boolean {
  if (!ctx) return false;
  if (ctx.isTenantAdmin || ctx.isSystemAdmin) return true;
  const sr = ctx.systemRole ?? "";
  if (sr === "TENANT_ADMIN" || sr === "SYSTEM_ADMIN" || sr === "PLATFORM_STAFF") return true;
  return (ctx.roleName ?? "").trim().toLowerCase() === "super admin";
}

/**
 * Single source of truth for whether the AI panel should be visible:
 * `Tenant.isAIEnabled` (via the existing tenant-profile query, so the panel
 * appears/disappears when an admin flips the toggle) AND the current user
 * being a Super Admin.
 */
export function useIsAIEnabled(): boolean {
  const { tenant } = useCurrentTenant();
  const ctx = useAppSelector(selectRbacContext);
  return Boolean((tenant as { isAIEnabled?: boolean } | null | undefined)?.isAIEnabled) && isSuperAdminContext(ctx);
}
